import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useCanEditRecipes } from "@/hooks/useCanEditRecipes";
import { useRecipe } from "@/hooks/recipes";
import { useRecipeViewMode } from "@/hooks/recipes/useRecipeViewMode";
import { Skeleton } from "@/components/ui/skeleton";
import {
  RecipeCardView,
  RecipeDetailView,
  RecipeMissingInfoBanner,
  RecipeViewModeToggle,
} from "@/components/recipes";
import { RecipeDetailHeader } from "@/components/recipes/detail";
import { RecipeMethodView } from "@/components/recipes/method";
import { missingForPublish } from "@/lib/recipes/publish-requirements";
import { CookModeDialog } from "@/components/recipes/dialogs/CookModeDialog";
import { DuplicateRecipeDialog } from "@/components/recipes/dialogs/DuplicateRecipeDialog";
import { CreateVersionDialog } from "@/components/recipes/dialogs/CreateVersionDialog";
import { DeleteSelectedDialog } from "@/components/recipes/dialogs/DeleteSelectedDialog";
import { useDuplicateRecipe, useCreateRecipeVersion, useDeleteRecipe } from "@/hooks/recipes/mutations";

export const Route = createFileRoute(
  "/(protected)/_layout/recipes/$recipeId",
)({
  component: RecipeDetailPage,
});

function RecipeDetailPage() {
  const { t } = useLingui();
  const { recipeId } = Route.useParams();
  const { isAuthenticated, hasPermission } = useAuth();
  const { viewMode, setViewMode } = useRecipeViewMode();
  // Recipes are viewable in both modes, but edited in Build mode only (YC-33).
  const canEditRecipes = useCanEditRecipes();
  const canManage = canEditRecipes && (hasPermission("recipe:update") || hasPermission("recipe:*"));
  const navigate = useNavigate();

  const [cookModeOpen, setCookModeOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [createVersionOpen, setCreateVersionOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data: recipe, isLoading, error } = useRecipe(parseInt(recipeId), isAuthenticated);

  const duplicateMutation = useDuplicateRecipe();
  const createVersionMutation = useCreateRecipeVersion();
  const deleteMutation = useDeleteRecipe();

  function handleDuplicateConfirm() {
    duplicateMutation.mutate(parseInt(recipeId), {
      onSuccess: (newRecipe) => {
        setDuplicateOpen(false);
        toast.success(t`Recipe duplicated successfully`);
        void navigate({ to: "/recipes/$recipeId", params: { recipeId: String(newRecipe.id) } });
      },
      onError: () => toast.error(t`Failed to duplicate recipe`),
    });
  }

  function handleCreateVersionConfirm(data: { versionNote: string; makeDefault: boolean }) {
    createVersionMutation.mutate(
      { id: parseInt(recipeId), data },
      {
        onSuccess: (newRecipe) => {
          setCreateVersionOpen(false);
          toast.success(t`New version created`);
          void navigate({ to: "/recipes/$recipeId", params: { recipeId: String(newRecipe.id) } });
        },
        onError: () => toast.error(t`Failed to create new version`),
      },
    );
  }

  function handleDeleteConfirm() {
    deleteMutation.mutate(parseInt(recipeId), {
      onSuccess: () => {
        setDeleteOpen(false);
        toast.success(t`Recipe deleted`);
        void navigate({ to: "/recipes", search: { tab: "prepRecipes", page: 1, pageSize: 50, status: "all" } });
      },
      onError: () => toast.error(t`Failed to delete recipe`),
    });
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-6 p-6">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-50 w-full rounded-xl" />
        <div className="flex items-start justify-between px-8">
          <div className="space-y-3">
            <Skeleton className="h-10 w-72" />
            <Skeleton className="h-4 w-48" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-13 w-24" />
            <Skeleton className="h-13 w-28" />
          </div>
        </div>
        <Skeleton className="h-10 w-full rounded-lg" />
        <Skeleton className="h-10 w-full rounded-lg" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div className="flex flex-1 flex-col gap-6 p-6">
        <Link
          to="/recipes"
          search={{ tab: "prepRecipes", page: 1, pageSize: 50, status: "all" }}
          className="inline-flex items-center gap-1.5 text-sm text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
        >
          <ArrowLeft className="size-4" />
          {t`Back to Recipes`}
        </Link>
        <div className="flex flex-col items-center gap-4 py-16">
          <p className="text-sm text-(--color-text-secondary)">
            {t`The recipe you're looking for doesn't exist or you don't have permission to view it.`}
          </p>
          <Link to="/recipes" search={{ tab: "prepRecipes", page: 1, pageSize: 50, status: "all" }}>
            <Button variant="outline" className="gap-1.5">
              <ArrowLeft className="size-4" />
              {t`Back to Recipes`}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-5">
      <RecipeDetailHeader
        recipe={recipe}
        mode="view"
        readOnly={!canEditRecipes}
        onEdit={() =>
          navigate({ to: "/recipes/$recipeId/edit", params: { recipeId }, search: { tab: "general" } })
        }
        onCookMode={() => setCookModeOpen(true)}
        onDuplicate={() => setDuplicateOpen(true)}
        onCreateVersion={() => setCreateVersionOpen(true)}
        onViewVersionHistory={() => toast.info(t`Version history coming soon`)}
        onShare={() => toast.info(t`Share coming soon`)}
        onExport={() => toast.info(t`Export coming soon`)}
        onDelete={() => setDeleteOpen(true)}
      />

      <CookModeDialog
        recipe={recipe}
        open={cookModeOpen}
        onOpenChange={setCookModeOpen}
      />

      <DuplicateRecipeDialog
        open={duplicateOpen}
        onOpenChange={setDuplicateOpen}
        onConfirm={handleDuplicateConfirm}
        isPending={duplicateMutation.isPending}
      />

      <CreateVersionDialog
        open={createVersionOpen}
        onOpenChange={setCreateVersionOpen}
        currentVersion={recipe.version}
        onConfirm={handleCreateVersionConfirm}
        isPending={createVersionMutation.isPending}
      />

      <DeleteSelectedDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        selectedCount={1}
        onConfirm={handleDeleteConfirm}
        isPending={deleteMutation.isPending}
      />

      {/* A draft is not automatically incomplete — say so only when it is (T-704). */}
      {!recipe.isPublished && missingForPublish(recipe).length > 0 && (
        <div className="px-3">
          <RecipeMissingInfoBanner missing={missingForPublish(recipe)} />
        </div>
      )}

      {/* Detailed or Card, and the choice sticks per user (T-806). */}
      <div className="px-6 print:hidden">
        <RecipeViewModeToggle value={viewMode} onChange={setViewMode} />
      </div>

      {viewMode === "card" ? (
        <RecipeCardView recipe={recipe} />
      ) : viewMode === "simple" || viewMode === "staged" ? (
        <RecipeMethodView
          recipe={recipe}
          layout={viewMode}
          canEdit={canManage}
        />
      ) : (
        <RecipeDetailView
          recipe={recipe}
          canManage={canManage}
          onEditTab={
            canEditRecipes
              ? (tab) =>
                  navigate({ to: "/recipes/$recipeId/edit", params: { recipeId }, search: { tab } })
              : undefined
          }
        />
      )}
    </div>
  );
}
